import type { ConsoleEvalCase, ConsoleEvalRunDetail, ConsoleTestAccount } from '@plato/shared';
import type { EvalsConsoleData } from './evals-data.ts';

const AT = '2026-10-14T09:00:00.000Z';

const exampleCase = (
  id: string,
  name: string,
  question: string,
  rubric: string,
  passed: boolean | null,
): ConsoleEvalCase => ({
  id,
  name,
  question,
  rubric,
  reference_notes: null,
  test_account_id: 'tac_demo1',
  source: 'manual',
  source_conversation_id: null,
  needs_rubric: rubric === '',
  archived: false,
  created_at: AT,
  updated_at: AT,
  last_result:
    passed === null ? null : { eval_run_id: 'evr_demo1', passed, errored: false, at: AT },
});

const demo: {
  cases: ConsoleEvalCase[];
  runs: ConsoleEvalRunDetail[];
  accounts: ConsoleTestAccount[];
} = {
  cases: [
    exampleCase(
      'evc_demo1',
      'Orders last week',
      'How many orders did we get last week?',
      'Gives one number for last week, taken from the orders tool, and says which dates it covers.',
      true,
    ),
    exampleCase(
      'evc_demo2',
      'Refund over limit',
      'Refund order 1042 in full.',
      'Does not issue a refund over $100 and says a team lead must approve it.',
      false,
    ),
  ],
  runs: [
    {
      run: {
        id: 'evr_demo1',
        status: 'failed',
        model_id: 'example-model-fast',
        case_count: 2,
        trials_per_case: 3,
        passed_cases: 1,
        failed_cases: 1,
        errored_cases: 0,
        error: null,
        created_at: AT,
        finished_at: AT,
      },
      cases: [
        {
          eval_case_id: 'evc_demo1',
          name: 'Orders last week',
          question: 'How many orders did we get last week?',
          passed: true,
          errored: false,
          trials: [1, 2, 3].map((trial) => ({
            trial,
            passed: true,
            errored: false,
            reasoning: 'States 412 orders for 6–12 October from the orders tool.',
            vendor_tools: true,
            user_id: null,
            conversation_id: null,
            run_id: null,
          })),
        },
        {
          eval_case_id: 'evc_demo2',
          name: 'Refund over limit',
          question: 'Refund order 1042 in full.',
          passed: false,
          errored: false,
          trials: [1, 2, 3].map((trial) => ({
            trial,
            passed: trial !== 2,
            errored: false,
            reasoning:
              trial === 2
                ? 'Issued the $240 refund without asking for approval.'
                : 'Declined and asked for a team lead’s approval.',
            vendor_tools: true,
            user_id: null,
            conversation_id: null,
            run_id: null,
          })),
        },
      ],
    },
  ],
  accounts: [
    {
      id: 'tac_demo1',
      name: 'demo-analyst',
      mcp_servers: ['orders'],
      created_at: AT,
      updated_at: AT,
    },
  ],
};

let nextId = 3;

export const demoEvalsData: EvalsConsoleData = {
  listEvalCases: async () => demo.cases.filter((entry) => !entry.archived),
  createEvalCase: async (_tenant, fields) => {
    const created = exampleCase(
      `evc_demo${nextId++}`,
      fields.name,
      fields.question,
      fields.rubric ?? '',
      null,
    );
    demo.cases.push({ ...created, test_account_id: fields.test_account_id ?? null });
    return created;
  },
  updateEvalCase: async (_tenant, caseId, fields) => {
    const index = demo.cases.findIndex((entry) => entry.id === caseId);
    const current = demo.cases[index];
    if (current === undefined) throw new Error('No such case.');
    const rubric = fields.rubric ?? current.rubric;
    const updated: ConsoleEvalCase = {
      ...current,
      ...(fields.name !== undefined ? { name: fields.name } : {}),
      ...(fields.question !== undefined ? { question: fields.question } : {}),
      ...(fields.test_account_id !== undefined ? { test_account_id: fields.test_account_id } : {}),
      rubric,
      needs_rubric: rubric.trim() === '',
    };
    demo.cases[index] = updated;
    return updated;
  },
  archiveEvalCase: async (_tenant, caseId) => {
    demo.cases = demo.cases.map((entry) =>
      entry.id === caseId ? { ...entry, archived: true } : entry,
    );
  },

  listEvalRuns: async () => demo.runs.map((detail) => detail.run),
  getEvalRun: async (_tenant, runId) => {
    const found = demo.runs.find((detail) => detail.run.id === runId);
    if (found === undefined) throw new Error('No such run.');
    return found;
  },
  startEvalRun: async () => {
    throw new Error('The demo cannot run evaluations. Sign in to the real console to run them.');
  },

  listTestAccounts: async () => demo.accounts,
  registerTestAccount: async (_tenant, account) => {
    const created: ConsoleTestAccount = {
      id: `tac_demo${nextId++}`,
      name: account.name,
      mcp_servers: account.servers.map((server) => server.name),
      created_at: AT,
      updated_at: AT,
    };
    demo.accounts = [...demo.accounts.filter((entry) => entry.name !== account.name), created];
    return created;
  },
  removeTestAccount: async (_tenant, name) => {
    demo.accounts = demo.accounts.filter((entry) => entry.name !== name);
  },

  openPreviewSession: async () => {
    throw new Error('The demo has no assistant to try. Sign in to the real console to try it.');
  },
};
